/**
 * Dashboards data hooks (frontend.md §5 step 14 — Tier-2).
 *
 * The dashboard *framework* is full v1; only 3 of 8 cards are fully built (Action Item
 * Burndown, Entity Frequency Heat Map, Project/Engagement Breakdown — frontend.md §1.3).
 * These hooks back those three plus the data the thin cards read for their headline
 * number. Each dashboard scopes by the shared date-range + scope controls (frontend.md
 * §2.1 `<DashboardScopeControls>`), passed in by the surface from the URL.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  detectContradictions,
  getActionItems,
  searchByMetadata,
} from '@/api';
import type {
  ActionItemList,
  ContradictionList,
  DateRange,
  EntityId,
  Filters,
  NotebookId,
  ResultList,
  Scope,
} from '@/types';
import { useErrorHandler } from './useErrorHandler';

/** The shared dashboard scope — every card reads the same date range + scope (frontend.md §2.1). */
export interface DashboardScope {
  dateRange: DateRange;
  scope: Scope;
}

/** Action Item Burndown + Project/Engagement Breakdown both read the action-item list. */
export function useActionItemsQuery(
  scope: DashboardScope,
  assignee?: EntityId | 'me',
): UseQueryResult<ActionItemList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['action-items', scope.dateRange, scope.scope, assignee],
    queryFn: async () => {
      try {
        return (
          await getActionItems({
            dateRange: scope.dateRange,
            assignee,
            // The scope notebook/tag ref is folded into `source` only when meaningful;
            // a corpus scope sends no source filter.
          })
        ).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

/** Contradiction Surface (thin card) reads the materialized `contradicts` edges. */
export function useContradictionsQuery(
  scope: DashboardScope,
): UseQueryResult<ContradictionList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['contradictions', scope.scope],
    queryFn: async () => {
      try {
        return (await detectContradictions({ scope: scope.scope })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

/** Translate a `DashboardScope` into a `search_by_metadata` Filters object.
 *
 * The dashboard scope carries a date range + a "what's in scope" selector (corpus / notebook
 * / tag / query). `Filters` is the wire shape `search_by_metadata` accepts. This is a pure
 * conversion — both the date range and the scope selector become filter rows. A "corpus"
 * scope means no notebook/tag narrowing.
 */
export function scopeToFilters(scope: DashboardScope): Filters {
  const filters: Filters = {};
  if (scope.dateRange.start) filters.dateFrom = scope.dateRange.start;
  if (scope.dateRange.end) filters.dateTo = scope.dateRange.end;
  if (scope.scope.kind === 'notebook' && scope.scope.ref) {
    filters.notebookIds = [scope.scope.ref as NotebookId];
  } else if (scope.scope.kind === 'tag' && scope.scope.ref) {
    filters.tagPaths = [scope.scope.ref];
  }
  return filters;
}

/** Scope-aware notes-in-window query — backs the new Activity / Cadence / Top-Notebooks /
 *  Recent-Activity cards. One fetch up to ``limit`` items in scope. All client-side
 *  bucketing / sorting / top-N is derived from this single response so multiple cards
 *  reading the same scope share the cache. */
export function useScopedNotesQuery(
  scope: DashboardScope,
  limit = 500,
): UseQueryResult<ResultList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['scoped-notes', scope.dateRange, scope.scope, limit],
    queryFn: async () => {
      try {
        const filters = scopeToFilters(scope);
        return (await searchByMetadata({ filters, limit })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}
